import React from 'react';
import { Link } from 'react-router-dom';
import styles from './About.module.css';

const About: React.FC = () => {
  // Single team member
  const founder = {
    name: 'Priyanshu Kumar',
    role: 'Founder & Chief Editor',
    image: 'https://i.postimg.cc/Z5rxDpTW/pri.png',
    bio: 'Passionate entrepreneur and storyteller with a vision to empower India\'s startup ecosystem. With over a decade of experience in journalism and business, I founded startuprahi to bridge the gap between aspiring entrepreneurs and success stories. My mission is to inspire, educate, and connect founders through authentic narratives and actionable insights.',
    quote: 'Every startup has a story worth telling, and every entrepreneur deserves to be heard.',
    signature: 'priyanshu',
    social: {
      linkedin: 'https://linkedin.com/in/dinesh-singh-saini',
      twitter: 'https://twitter.com/dineshsaini',
      instagram: 'https://instagram.com/dinesh.saini',
      email: 'dinesh@startuprahi.com'
    }
  };

  const values = [
    {
      icon: '🎯',
      title: 'Accuracy',
      description: 'We deliver verified, factual information you can trust.'
    },
    {
      icon: '⚡',
      title: 'Speed',
      description: 'Breaking news and updates delivered in real-time.'
    },
    {
      icon: '🤝',
      title: 'Community',
      description: 'Building a network of entrepreneurs and innovators.'
    },
    {
      icon: '💡',
      title: 'Innovation',
      description: 'Highlighting groundbreaking ideas and technologies.'
    }
  ];

  const stats = [
    { number: '50K+', label: 'Monthly Readers' },
    { number: '1000+', label: 'Startups Featured' },
    { number: '500+', label: 'Success Stories' },
    { number: '100+', label: 'Expert Contributors' }
  ];

  return (
    <div className={styles.aboutPage}>
      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.heroContainer}>
          <div className={styles.heroContent}>
            <span className={styles.badge}>About startuprahi</span>
            <h1 className={styles.heroTitle}>
              Empowering <span className={styles.gradient}>Entrepreneurs</span> with 
              Stories that <span className={styles.gradient}>Inspire</span>
            </h1>
            <p className={styles.heroSubtitle}>
              India's leading platform for startup news, business insights, and entrepreneurial success stories. 
              We bring you the latest trends, innovations, and strategic guidance to help you succeed.
            </p>
            <div className={styles.heroButtons}>
              <Link to="/subscribe" className={styles.primaryButton}>
                Subscribe Now
              </Link>
              <Link to="/category/trending" className={styles.secondaryButton}>
                Explore Stories
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className={styles.statsSection}>
        <div className={styles.container}>
          <div className={styles.statsGrid}>
            {stats.map((stat, index) => (
              <div key={index} className={styles.statCard}>
                <div className={styles.statNumber}>{stat.number}</div>
                <div className={styles.statLabel}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className={styles.section}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionBadge}>Who We Are</span>
            <h2 className={styles.sectionTitle}>Your Trusted Source for Startup News</h2>
            <p className={styles.sectionSubtitle}>
              Founded in 2020, startuprahi has become India's go-to platform for entrepreneurs, 
              investors, and business enthusiasts seeking authentic startup stories and market insights.
            </p>
          </div>
          
          <div className={styles.contentGrid}>
            <div className={styles.contentCard}>
              <div className={styles.cardIcon}>📰</div>
              <h3>News & Updates</h3>
              <p>
                Breaking news from the startup ecosystem, funding announcements, product launches, 
                and industry trends delivered fresh every day.
              </p>
            </div>
            <div className={styles.contentCard}>
              <div className={styles.cardIcon}>🚀</div>
              <h3>Success Stories</h3>
              <p>
                In-depth interviews with successful founders, their journeys, challenges, 
                and lessons learned along the way.
              </p>
            </div>
            <div className={styles.contentCard}>
              <div className={styles.cardIcon}>📊</div>
              <h3>Market Insights</h3>
              <p>
                Data-driven analysis, market reports, and strategic insights to help you 
                make informed business decisions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className={styles.missionVision}>
        <div className={styles.container}>
          <div className={styles.mvGrid}>
            <div className={styles.mvCard}>
              <div className={styles.mvIcon}>🎯</div>
              <h3 className={styles.mvTitle}>Our Mission</h3>
              <p className={styles.mvText}>
                To democratize access to startup knowledge and create a thriving ecosystem 
                where entrepreneurs can learn, connect, and grow. We aim to bridge the gap 
                between aspiring founders and successful businesses through authentic storytelling 
                and actionable insights.
              </p>
            </div>
            <div className={styles.mvCard}>
              <div className={styles.mvIcon}>🔭</div>
              <h3 className={styles.mvTitle}>Our Vision</h3>
              <p className={styles.mvText}>
                To become the world's most trusted platform for startup news and entrepreneurial 
                content, inspiring millions to chase their dreams and build successful businesses. 
                We envision a future where every entrepreneur has access to the resources they need to succeed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className={styles.section}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionBadge}>What We Do</span>
            <h2 className={styles.sectionTitle}>Comprehensive Startup Coverage</h2>
          </div>

          <div className={styles.whatWeDoGrid}>
            <div className={styles.wdCard}>
              <div className={styles.wdNumber}>01</div>
              <h3>Daily News Coverage</h3>
              <p>
                We publish 10+ articles daily covering funding rounds, acquisitions, product launches, 
                policy changes, and everything happening in the startup world.
              </p>
            </div>
            <div className={styles.wdCard}>
              <div className={styles.wdNumber}>02</div>
              <h3>Founder Interviews</h3>
              <p>
                Exclusive conversations with successful entrepreneurs, uncovering their strategies, 
                failures, and the lessons that shaped their journey.
              </p>
            </div>
            <div className={styles.wdCard}>
              <div className={styles.wdNumber}>03</div>
              <h3>Expert Analysis</h3>
              <p>
                Industry experts and analysts provide deep-dive reports on market trends, 
                emerging sectors, and investment opportunities.
              </p>
            </div>
            <div className={styles.wdCard}>
              <div className={styles.wdNumber}>04</div>
              <h3>Community Building</h3>
              <p>
                Connecting entrepreneurs through events, webinars, and networking opportunities 
                to foster collaboration and growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className={styles.valuesSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionBadge}>Our Values</span>
            <h2 className={styles.sectionTitle}>What Drives Us</h2>
          </div>

          <div className={styles.valuesGrid}>
            {values.map((value, index) => (
              <div key={index} className={styles.valueCard}>
                <div className={styles.valueIcon}>{value.icon}</div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Section - Beautiful Horizontal Card */}
      <section className={styles.founderSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionBadge}>Meet the Founder</span>
            <h2 className={styles.sectionTitle}>The Visionary Behind startuprahi</h2>
          </div>

          <div className={styles.founderCard}>
            {/* Left Side - Image */}
            <div className={styles.founderImageWrapper}>
              <div className={styles.founderImageContainer}>
                <img src={founder.image} alt={founder.name} />
                <div className={styles.imageOverlay}></div>
              </div>
              <div className={styles.founderRole}>{founder.role}</div>
            </div>

            {/* Right Side - Content */}
            <div className={styles.founderContent}>
              <h3 className={styles.founderName}>{founder.name}</h3>
              
              <div className={styles.founderQuote}>
                <svg className={styles.quoteIcon} width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z"/>
                </svg>
                <p>{founder.quote}</p>
              </div>

              <p className={styles.founderBio}>{founder.bio}</p>

              {/* Social Links */}
              <div className={styles.founderSocial}>
                <a href={founder.social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                <a href={founder.social.twitter} target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                </a>
                <a href={founder.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
                  </svg>
                </a>
                <a href={`mailto:${founder.social.email}`} aria-label="Email">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                </a>
              </div>

              {/* Signature */}
              <div className={styles.signature}>
                <svg className={styles.signatureLine} width="100" height="2" viewBox="0 0 100 2">
                  <line x1="0" y1="1" x2="100" y2="1" stroke="currentColor" strokeWidth="1"/>
                </svg>
                <span className={styles.signatureText}>{founder.signature}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default About;